import type { CompanyKpiRepository } from "../../repositories/company/company-kpi-repository";
import { AppError } from "../../../shared/utils/error-handler";
import type {
  CompanyKpiMachine,
  CompanyKpiSlot,
  CompanyKpiSlotStat,
  ComputeRequest,
  ComputeResponse,
  CreateKpiSlotRequest,
  KpiStatisticsResponse,
  MesDashboardCard,
  ProductionLogResponse,
  SaveCardRequest,
  UpdateKpiSlotRequest,
} from "../../types/company/company-kpi";

export class CompanyKpiUseCase {
  constructor(private companyKpiRepository: CompanyKpiRepository) {}

  async getStatistics(): Promise<KpiStatisticsResponse> {
    try {
      return await this.companyKpiRepository.getStatistics();
    } catch (error) {
      throw new AppError(
        String(error) || "KPI 통계를 못했습니다.",
        "KPI_STATISTICS_GET_FAILED",
        error,
      );
    }
  }

  async getStatisticsPeriod(
    startDate: string,
    endDate: string,
  ): Promise<KpiStatisticsResponse> {
    try {
      return await this.companyKpiRepository.getStatisticsPeriod(startDate, endDate);
    } catch (error) {
      throw new AppError(
        String(error) || "KPI 통계를 못했습니다.",
        "KPI_STATISTICS_GET_FAILED",
        error,
      );
    }
  }

  async getProductionLogs(
    date: string,
    machineIds?: number[],
  ): Promise<ProductionLogResponse> {
    try {
      return await this.companyKpiRepository.getProductionLogs(date, machineIds);
    } catch (error) {
      throw new AppError(
        String(error) || "생산량 로그를 가져오지 못했습니다.",
        "PRODUCTION_LOGS_GET_FAILED",
        error,
      );
    }
  }

  async getSlots(): Promise<CompanyKpiSlot[]> {
    try {
      return await this.companyKpiRepository.getSlots();
    } catch (error) {
      throw new AppError(
        String(error) || "KPI 항목을 가져오지 못했습니다.",
        "KPI_SLOTS_GET_FAILED",
        error,
      );
    }
  }

  async updateSlot(
    id: number,
    body: UpdateKpiSlotRequest,
  ): Promise<CompanyKpiSlot> {
    try {
      return await this.companyKpiRepository.updateSlot(id, body);
    } catch (error) {
      throw new AppError(
        String(error) || "KPI 항목 수정에 실패했습니다.",
        "KPI_SLOT_UPDATE_FAILED",
        error,
      );
    }
  }

  async createSlot(body: CreateKpiSlotRequest): Promise<CompanyKpiSlot> {
    try {
      return await this.companyKpiRepository.createSlot(body);
    } catch (error) {
      throw new AppError(
        String(error) || "KPI 항목 추가에 실패했습니다.",
        "KPI_SLOT_CREATE_FAILED",
        error,
      );
    }
  }

  async deleteSlot(id: number): Promise<void> {
    try {
      await this.companyKpiRepository.deleteSlot(id);
    } catch (error) {
      throw new AppError(
        String(error) || "KPI 항목 삭제에 실패했습니다.",
        "KPI_SLOT_DELETE_FAILED",
        error,
      );
    }
  }

  async getSlotStatistics(
    startDate: string,
    endDate: string,
    machineId?: number,
  ): Promise<CompanyKpiSlotStat[]> {
    try {
      return await this.companyKpiRepository.getSlotStatistics(
        startDate,
        endDate,
        machineId,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "KPI 통계를 가져오지 못했습니다.",
        "KPI_SLOT_STATISTICS_FAILED",
        error,
      );
    }
  }

  async getMachines(): Promise<CompanyKpiMachine[]> {
    try {
      return await this.companyKpiRepository.getMachines();
    } catch (error) {
      throw new AppError(
        String(error) || "장비 목록을 가져오지 못했습니다.",
        "KPI_MACHINES_FAILED",
        error,
      );
    }
  }

  // ─── KPI 2.6 카드 대시보드 ──────────────────────────────────────────────────

  async compute(body: ComputeRequest): Promise<ComputeResponse> {
    try {
      return await this.companyKpiRepository.compute(body);
    } catch (error) {
      throw new AppError(
        String(error) || "동적 계산에 실패했습니다.",
        "KPI_COMPUTE_FAILED",
        error,
      );
    }
  }

  async getCards(): Promise<MesDashboardCard[]> {
    try {
      return await this.companyKpiRepository.getCards();
    } catch (error) {
      throw new AppError(
        String(error) || "카드 목록을 가져오지 못했습니다.",
        "KPI_CARDS_GET_FAILED",
        error,
      );
    }
  }

  async createCard(body: SaveCardRequest): Promise<MesDashboardCard> {
    try {
      return await this.companyKpiRepository.createCard(body);
    } catch (error) {
      throw new AppError(
        String(error) || "카드 추가에 실패했습니다.",
        "KPI_CARD_CREATE_FAILED",
        error,
      );
    }
  }

  async updateCard(id: number, body: SaveCardRequest): Promise<MesDashboardCard> {
    try {
      return await this.companyKpiRepository.updateCard(id, body);
    } catch (error) {
      throw new AppError(
        String(error) || "카드 수정에 실패했습니다.",
        "KPI_CARD_UPDATE_FAILED",
        error,
      );
    }
  }

  async deleteCard(id: number): Promise<void> {
    try {
      await this.companyKpiRepository.deleteCard(id);
    } catch (error) {
      throw new AppError(
        String(error) || "카드 삭제에 실패했습니다.",
        "KPI_CARD_DELETE_FAILED",
        error,
      );
    }
  }

  async reorderCards(cardIds: number[]): Promise<MesDashboardCard[]> {
    try {
      return await this.companyKpiRepository.reorderCards(cardIds);
    } catch (error) {
      throw new AppError(
        String(error) || "카드 순서 변경에 실패했습니다.",
        "KPI_CARDS_REORDER_FAILED",
        error,
      );
    }
  }
}